import { useEffect, useRef, useState } from 'react';

import {
  Button,
  Divider,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
  VStack
} from '@chakra-ui/react';

import { FiTarget } from 'react-icons/fi';
import { AsyncSelect, OptionsOrGroups } from 'chakra-react-select';
import { StyledToast } from './StyledToast';

const formatLocationOptions = (p: any) => {
  const options = p.map((org: any) => {
    return {
      value: org.place_id,
      label: org.description
    };
  });
  return options;
};

export interface LocationResults {
  loc?: string;
  lat?: number;
  lng?: number;
}

interface LocationSearchProps {
  isOpen: boolean;
  onClose: (value: LocationResults) => void;
}

export const LocationSearch = ({ isOpen, onClose }: LocationSearchProps) => {
  const [gettingCurrentLocation, setGettingCurrentLocation] = useState<boolean>(false);
  const toast = useToast();
  const autocompleteServiceRef = useRef<google.maps.places.AutocompleteService | null>(null);
  const geocoderRef = useRef<google.maps.Geocoder | null>(null);

  const handleLocationError = () => {
    toast({
      position: 'top-right',
      duration: 4000,
      render: ({ onClose }) => (
        <StyledToast
          onClose={onClose}
          type="info"
          title="Location Search Error"
          description="There was an error searching for locations. Please refresh the page."
        />
      )
    });
  };

  // intermittently, the google maps instance is not available on page load. The interval is to check for it
  useEffect(() => {
    if (window.google && window.google.maps) {
      autocompleteServiceRef.current = new google.maps.places.AutocompleteService();
      geocoderRef.current = new google.maps.Geocoder();
    } else {
      let checkCount = 0;

      const interval = setInterval(() => {
        if (window.google && window.google.maps) {
          autocompleteServiceRef.current = new google.maps.places.AutocompleteService();
          geocoderRef.current = new google.maps.Geocoder();
          clearInterval(interval);
        }

        checkCount += 1;

        if (checkCount >= 20) {
          clearInterval(interval);
        }
      }, 1000);

      return () => clearInterval(interval);
    }
  }, []);

  const searchForLocations = async (inputValue: string) => {
    const request = {
      input: inputValue,
      types: ['geocode'],
      componentRestrictions: { country: 'us' }
    };

    if (!autocompleteServiceRef.current?.getPlacePredictions) {
      handleLocationError();
      return [];
    }

    const opts = await autocompleteServiceRef.current?.getPlacePredictions(request);
    return formatLocationOptions(opts?.predictions ?? []);
  };

  const geocode = async (address: string) => {
    if (!autocompleteServiceRef.current?.getPlacePredictions) {
      handleLocationError();
      return;
    }

    const data = await geocoderRef.current?.geocode({ address });
    if (data?.results) {
      onClose({
        loc: data.results[0].formatted_address,
        lat: data.results[0].geometry.location.lat(),
        lng: data.results[0].geometry.location.lng()
      });
    }
  };

  const getCurrentLocation = async () => {
    setGettingCurrentLocation(true);
    if (navigator.geolocation) {
      if (!autocompleteServiceRef.current?.getPlacePredictions) {
        handleLocationError();
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const lat = pos.coords.latitude;
          const lng = pos.coords.longitude;
          const data = await geocoderRef.current?.geocode({ location: { lat, lng } });
          if (data?.results) {
            onClose({
              loc: data.results[0].formatted_address,
              lat,
              lng
            });
          }
          setGettingCurrentLocation(false);
        },
        (error) => {
          console.error('Geolocation error: ', error);
          setGettingCurrentLocation(false);
        }
      );
    } else {
      setGettingCurrentLocation(false);
    }
  };

  const loadOptions = async (
    inputValue: string,
    callback: (options: any) => void
  ): Promise<OptionsOrGroups<never, never>> => {
    try {
      const result = await searchForLocations(inputValue);
      callback(result);
      return result;
    } catch (_e) {
      handleLocationError();
      return [];
    }
  };

  const handleModalClose = () => onClose({});

  return (
    <Modal isOpen={isOpen} onClose={handleModalClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Set your location</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <Text>Enter the zipcode or address where you'd like to search for a pharmacy.</Text>
          <Button
            w="full"
            leftIcon={<FiTarget />}
            mt={5}
            onClick={async () => getCurrentLocation()}
            isLoading={gettingCurrentLocation}
            loadingText="Getting current location"
          >
            Use my current location
          </Button>
          <HStack spacing={2} mt={5}>
            <Divider />
            <Text fontWeight="medium">OR</Text>
            <Divider />
          </HStack>
          <VStack spacing={1} align="start">
            <Text mt={5} fontWeight="medium">
              Find a location
            </Text>
            <VStack w="full" align="stretch">
              <Text pb={0} mt={0} fontSize="sm">
                Enter a zipcode or address
              </Text>
              <AsyncSelect
                placeholder=""
                loadOptions={loadOptions}
                defaultOptions={[]}
                isClearable
                menuPlacement="auto"
                onChange={async (val) => {
                  if (val) {
                    // @ts-ignore
                    await geocode(val.label);
                  }
                }}
              />
            </VStack>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
